import { useState } from 'react';
import { ArrowUpDown, CalendarDays, ChevronDown, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { MessagePreview } from '../api/conversations';
import { neighbours } from '../lib/eventMonths';
import type { EventMonthGroup } from '../lib/eventMonths';
import { formatTimestamp } from '../lib/datetime';
import { UnreadDot } from './UnreadDot';
import { MonthBar } from './MonthBar';

/**
 * One thread as a list draws it, whichever door the list came through.
 *
 * A member's row and a parent's row differ in what they can say about a thread and not in how a
 * thread is said — so they normalise to this and the rendering below is the only one in the
 * product. Rule 13b is about `/family` never growing its own rules; a second copy of the row is how
 * it would grow its own *manners*, and this component is already the second thing (after
 * `ParticipantStrip`) that had to be extracted because two screens had begun to drift.
 */
export interface ThreadListRow {
  id: string;
  title: string;
  preview: MessagePreview | null;
  lastMessageAt: string | null;
  unreadCount: number;
  /**
   * A second caption under the preview: which unit it is in, which of your children are in it, why
   * you may read it. Absent on a member's own list, where all three would be saying "yours".
   */
  meta?: string | null;
}

/**
 * How many rows of a section are drawn before the rest go behind *Show N more*.
 *
 * Bounded because the dev troop carries well over a hundred live groups and an adult officer
 * monitors every one of them — the third screen in this product to meet a list it must not render
 * whole. The bound is not only a dev-data concern: a row per group reads fine for four patrols and
 * is unusable for twenty. Nothing is truncated, only folded, because a thread somebody has to
 * answer must stay reachable.
 */
const DRAWN = 6;

/**
 * One collapsible section of a thread list, bounded.
 *
 * The heading carries the count of what the section holds rather than the number drawn — the same
 * trade `ParticipantStrip` makes with its `+N` pill. `partial` says the section was built from a
 * feed with more behind it, so the count is what has been loaded rather than what exists, and says
 * so instead of implying a total it does not know.
 */
export function ThreadSection({
  icon: Icon,
  title,
  rows,
  unread,
  partial = false,
  onOpen,
}: {
  icon: LucideIcon;
  title: string;
  rows: ThreadListRow[];
  unread: number;
  partial?: boolean;
  onOpen: (conversationId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(true);

  if (rows.length === 0) {
    return null;
  }

  const drawn = expanded ? rows : rows.slice(0, DRAWN);
  const hidden = rows.length - drawn.length;

  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left"
        style={{
          background: 'none',
          border: 'none',
          padding: 'var(--space-2) 0',
          minHeight: 'var(--tap-min)',
          color: 'var(--color-text-primary)',
        }}
      >
        {/* Chevron and text, never colour alone — the rule the event palette follows for the same
            reason. */}
        {open ? (
          <ChevronDown size={16} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />
        ) : (
          <ChevronRight size={16} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />
        )}
        <Icon size={16} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />
        <span className="t-body-strong">{title}</span>
        <span className="t-caption tabular" style={{ color: 'var(--color-text-secondary)' }}>
          {partial ? `${rows.length} loaded` : rows.length}
        </span>
        {unread > 0 && <UnreadDot count={unread} />}
      </button>

      {open && (
        <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
          {drawn.map((row) => (
            <Row key={row.id} row={row} onOpen={() => onOpen(row.id)} />
          ))}

          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="t-caption text-left"
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-text-secondary)',
                padding: 'var(--space-2) var(--space-1)',
                minHeight: 'var(--tap-min)',
                textDecoration: 'underline',
              }}
            >
              Show {hidden} more
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function Row({ row, onOpen }: { row: ThreadListRow; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-start justify-between gap-4 text-left"
      style={{
        background: 'var(--color-surface-raised)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--elevation-raised)',
        border: 'none',
        padding: 'var(--space-4)',
        minHeight: 'var(--tap-min)',
      }}
    >
      <span className="min-w-0">
        <span
          className="t-body-strong inline-flex items-center gap-2"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {row.unreadCount > 0 && <UnreadDot count={row.unreadCount} />}
          <span>{row.title}</span>
        </span>

        <span
          className="t-caption"
          style={{
            color: 'var(--color-text-secondary)',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.preview ? (
            row.preview.isRemoved ? (
              // The tombstone, never the words. A list must not be the one surface still holding a
              // removed message's text.
              <em>{row.preview.authorDisplayName} &mdash; message removed</em>
            ) : (
              <>
                {row.preview.authorDisplayName}: {row.preview.body}
              </>
            )
          ) : (
            <em>No messages yet</em>
          )}
        </span>

        {row.meta && (
          <span className="t-caption" style={{ color: 'var(--color-text-muted)', display: 'block' }}>
            {row.meta}
          </span>
        )}
      </span>

      {row.lastMessageAt && (
        <span
          className="t-caption tabular"
          style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}
        >
          {formatTimestamp(row.lastMessageAt)}
        </span>
      )}
    </button>
  );
}

/**
 * Which question the events section is answering.
 *
 * Two genuinely different ones, which is why it is a control rather than a decision somebody made
 * once. *What is coming up* is the question when planning the month, and it wants a calendar.
 * *What is being talked about* is the question on a Thursday evening, and it wants a flat list —
 * months would fight it, because the whole point is that a thread from last March can be the most
 * important row on the page.
 */
export type EventSort = 'messages' | 'date';

/** One kind of event, within one month. */
export interface EventGroup {
  /** The unit's own name for this kind of event, or null where an event has no type. */
  kind: string | null;
  colorKey: string | null;
  rows: ThreadListRow[];
}

/** Kinds drawn within one month before the rest go behind *Show N more kinds*. */
const KINDS_DRAWN = 6;

/**
 * The events section: a window of whole months, each holding the unit's own kinds.
 *
 * Kinds are named from the unit's event types, so nothing here spells out "Campout" — the same rule
 * that keeps "Patrol" out of the codebase (rule 16), applied to a vocabulary each unit invents for
 * itself. An event with no type gets a plain heading rather than being hidden.
 */
export function EventThreadSection({
  months,
  recent,
  index,
  sort,
  unread,
  busy = false,
  onSortChange,
  onAnchorChange,
  onOpen,
}: {
  months: EventMonthGroup[];
  recent: ThreadListRow[];
  /** Every month with something in it, for the picker and the arrows. */
  index: { month: string; count: number }[];
  sort: EventSort;
  unread: number;
  busy?: boolean;
  onSortChange: (sort: EventSort) => void;
  onAnchorChange: (month: string) => void;
  onOpen: (conversationId: string) => void;
}) {
  const [open, setOpen] = useState(true);

  if (months.length === 0 && recent.length === 0) {
    return null;
  }

  const { previous, next } = neighbours(index, months);
  const total = index.reduce((sum, m) => sum + m.count, 0);

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex items-center gap-2 text-left"
          style={{
            background: 'none',
            border: 'none',
            padding: 'var(--space-2) 0',
            minHeight: 'var(--tap-min)',
            color: 'var(--color-text-primary)',
          }}
        >
          {open ? (
            <ChevronDown size={16} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />
          ) : (
            <ChevronRight size={16} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />
          )}
          <CalendarDays size={16} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />
          <span className="t-body-strong">Events</span>
          <span className="t-caption tabular" style={{ color: 'var(--color-text-secondary)' }}>
            {total}
          </span>
          {unread > 0 && <UnreadDot count={unread} />}
        </button>

        {open && <SortToggle sort={sort} onChange={onSortChange} />}
      </div>

      {open && sort === 'date' && (
        <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
          <MonthBar
            index={index}
            shown={months}
            previous={previous}
            next={next}
            busy={busy}
            onAnchorChange={onAnchorChange}
          />

          {months.map((month) => (
            <MonthBlock key={month.month} month={month} onOpen={onOpen} />
          ))}
        </div>
      )}

      {open && sort === 'messages' && (
        <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
          {/* Flat, and bounded by the server rather than folded here: this view exists to answer
              "what moved", and a *Show more* under it would be offering older news. */}
          {recent.map((row) => (
            <Row key={row.id} row={row} onOpen={() => onOpen(row.id)} />
          ))}

          {recent.length === 0 && (
            <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              Nothing has been said on any event yet.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function MonthBlock({
  month,
  onOpen,
}: {
  month: EventMonthGroup;
  onOpen: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const drawn = expanded ? month.groups : month.groups.slice(0, KINDS_DRAWN);
  const hidden = month.groups.length - drawn.length;
  const count = month.groups.reduce((n, g) => n + g.rows.length, 0);

  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
      <div
        className="flex items-baseline gap-2"
        style={{
          borderBottom: '1px solid var(--color-border-subtle)',
          paddingBottom: 'var(--space-1)',
        }}
      >
        <h3 className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
          {month.label}
        </h3>
        <span className="t-caption tabular" style={{ color: 'var(--color-text-secondary)' }}>
          {count}
        </span>
      </div>

      {drawn.map((group) => (
        <div
          key={group.kind ?? '(no kind)'}
          className="flex flex-col"
          style={{ gap: 'var(--space-2)' }}
        >
          <div className="flex items-center gap-2">
            {/* The kind's colour as a small mark beside its name, never as a fill and never alone:
                the name is the label, and this only helps a scanning eye find it again — the same
                job the 4px rule does on a calendar row. */}
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: 'var(--radius-full)',
                background: group.colorKey
                  ? `var(--color-event-${group.colorKey})`
                  : 'var(--color-text-muted)',
                flexShrink: 0,
              }}
            />
            <span className="t-label" style={{ color: 'var(--color-text-muted)' }}>
              {group.kind ?? 'No kind set'}
            </span>
            <span className="t-caption tabular" style={{ color: 'var(--color-text-secondary)' }}>
              {group.rows.length}
            </span>
          </div>

          <BoundedRows rows={group.rows} onOpen={onOpen} />
        </div>
      ))}

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="t-caption text-left"
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary)',
            padding: 'var(--space-2) var(--space-1)',
            minHeight: 'var(--tap-min)',
            textDecoration: 'underline',
          }}
        >
          Show {hidden} more {hidden === 1 ? 'kind' : 'kinds'}
        </button>
      )}
    </div>
  );
}

/**
 * Two buttons rather than a `<select>`: there are exactly two answers, both worth reading at a
 * glance, and which one is active is carried by weight and fill together rather than by colour
 * alone.
 */
function SortToggle({ sort, onChange }: { sort: EventSort; onChange: (sort: EventSort) => void }) {
  return (
    <div role="group" aria-label="Sort events" className="flex items-center gap-1">
      <ArrowUpDown size={14} aria-hidden="true" style={{ color: 'var(--color-text-muted)' }} />

      {(['date', 'messages'] as const).map((option) => {
        const selected = sort === option;

        return (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            aria-pressed={selected}
            className="t-caption"
            style={{
              background: selected ? 'var(--color-surface-sunken)' : 'none',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-1) var(--space-2)',
              minHeight: 'var(--tap-min)',
              color: selected ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              fontWeight: selected ? 600 : 400,
            }}
          >
            {option === 'date' ? 'Event date' : 'Recent messages'}
          </button>
        );
      })}
    </div>
  );
}

/** The same bound the flat sections use, applied within one kind. */
function BoundedRows({ rows, onOpen }: { rows: ThreadListRow[]; onOpen: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);

  const drawn = expanded ? rows : rows.slice(0, DRAWN);
  const hidden = rows.length - drawn.length;

  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
      {drawn.map((row) => (
        <Row key={row.id} row={row} onOpen={() => onOpen(row.id)} />
      ))}

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="t-caption text-left"
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-text-secondary)',
            padding: 'var(--space-2) var(--space-1)',
            minHeight: 'var(--tap-min)',
            textDecoration: 'underline',
          }}
        >
          Show {hidden} more
        </button>
      )}
    </div>
  );
}
